import { createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";
import applyItemAttributeOverrides from "@tSurya/Handlers/Configurator/ApplyItemAttributeOverrides";
import { updateUrlWithSelections } from "@tSurya/Handlers/Configurator/ConfiguratorUrlUtils";
import { postConfigurationData } from "@tSurya/Services/ConfiguratorService";
import { ConfigurationDataResultModel } from "@tSurya/Store/Data/ConfiguratorModels";
import { findMatchingStyleId } from "@tSurya/Store/Data/ConfiguratorSelectors";
import { CustomHandler } from "@tSurya/Store/CustomHandlerCreator/CustomHandlerCreator";

interface UpdateConfiguratorSelectionParameter {
    optionId: string;
    valueId: string;
    onSuccess?: () => void;
    onError?: (error: string) => void;
}

type Props = {
    mergedSelections: Record<string, string>;
    configurationResult: ConfigurationDataResultModel;
};

type HandlerType = CustomHandler<UpdateConfiguratorSelectionParameter, Props>;

export const MergeSelection: HandlerType = props => {
    var currentSelections = props.getState().SURData.configuratorStore.selections;
    props.mergedSelections = {
        ...currentSelections,
        [props.parameter.optionId]: props.parameter.valueId,
    };
};

export const ValidateProductId: HandlerType = props => {
    var productConfig = props.getState().SURData.configuratorStore.productConfig;
    if (!productConfig?.productId) {
        var errorMessage = "No configurator product loaded";
        props.dispatch({
            type: "Custom/Configurator/FailUpdate",
            error: errorMessage,
        });
        props.parameter.onError?.(errorMessage);
        return false;
    }
};

export const DispatchBeginUpdate: HandlerType = props => {
    props.dispatch({
        type: "Custom/Configurator/BeginUpdate",
    });
};

export const PostConfigurationData: HandlerType = async props => {
    var state = props.getState();
    var productId = state.SURData.configuratorStore.productConfig!.productId;

    try {
        props.configurationResult = await postConfigurationData({
            productId,
            selections: props.mergedSelections,
        });
    } catch (e) {
        var errorMessage = e instanceof Error ? e.message : "Failed to update configuration";
        props.dispatch({
            type: "Custom/Configurator/FailUpdate",
            error: errorMessage,
        });
        props.parameter.onError?.(errorMessage);
        return false;
    }
};

export const DispatchCompleteUpdate: HandlerType = props => {
    // Use the backend's validated selections (accounts for dependency rules)
    props.dispatch({
        type: "Custom/Configurator/CompleteUpdate",
        selections: props.configurationResult.selections,
        configurationResult: props.configurationResult,
    });
};

export const ApplyOverrides: HandlerType = props => {
    props.dispatch(applyItemAttributeOverrides({}));
};

export const DetectStyleDivergence: HandlerType = props => {
    var state = props.getState();
    var styles = state.SURData.configuratorStore.productConfig?.preConfiguredStyles;
    if (!styles || styles.length === 0) {
        return;
    }
    var matchedStyleId = findMatchingStyleId(styles, props.configurationResult.selections);
    props.dispatch({
        type: "Custom/Configurator/SetSelectedStyleId",
        selectedStyleId: matchedStyleId,
    });
};

export const SyncUrlAfterUpdate: HandlerType = props => {
    var options = props.getState().SURData.configuratorStore.productConfig?.options;
    if (options) {
        var knownOptionIds = new Set(options.map(o => o.optionId));
        updateUrlWithSelections(props.configurationResult.selections, knownOptionIds);
    }
};

export const InvokeOnSuccess: HandlerType = props => {
    props.parameter.onSuccess?.();
};

export const chain = [
    MergeSelection,
    ValidateProductId,
    DispatchBeginUpdate,
    PostConfigurationData,
    DispatchCompleteUpdate,
    ApplyOverrides,
    DetectStyleDivergence,
    SyncUrlAfterUpdate,
    InvokeOnSuccess,
];

const updateConfiguratorSelection = createHandlerChainRunner(chain, "UpdateConfiguratorSelection");
export default updateConfiguratorSelection;
